/** @typedef {{ key: string, label: string, kind: string, group: string }} RegistryEntry */

const REGION_MAP = [
  { key: 'region:invite', selector: '.mc-invite', label: 'Invite (root)', group: 'Regions' },
  { key: 'region:copy', selector: '.mc-copy', label: 'Copy column', group: 'Regions' },
  { key: 'region:gallery', selector: '.mc-gallery', label: 'Photo gallery', group: 'Regions' },
];

const BLOCK_SELECTORS = [
  { key: 'block:badges', selector: '.mc-badges', label: 'Badges', group: 'Blocks' },
  { key: 'block:phase-badges', selector: '.mc-phase-badges', label: 'Phase badges', group: 'Blocks' },
];

export function isLogoImage(el) {
  if (!el || el.tagName?.toLowerCase() !== 'img') return false;
  if (el.hasAttribute('data-photo-slot')) return false;
  if (el.hasAttribute('data-logo-slot')) return true;
  if (el.classList?.contains('mc-brand')) return true;
  return Boolean(el.closest?.('.mc-logo-wrap'));
}

export function findLogoImage(root) {
  if (!root) return null;
  const logos = queryLogoImages(root);
  return logos[0] || null;
}

/** All brand/logo images in the artboard (single logo_binding applies to each). */
export function queryLogoImages(root) {
  if (!root) return [];
  return [...root.querySelectorAll('img')].filter(isLogoImage);
}

export function elementLayoutKeyFromElement(el) {
  if (!el) return null;
  const editable = el.getAttribute?.('data-editable');
  if (editable) return editable;
  const eid = el.getAttribute?.('data-mc-eid');
  if (eid) return eid;
  const slot = el.getAttribute?.('data-photo-slot');
  if (slot && el.matches?.('img[data-photo-slot]')) return `slot:${slot}`;
  if (isLogoImage(el)) return 'logo:brand';
  if (el.classList?.contains('mc-badges')) return 'block:badges';
  if (el.classList?.contains('mc-phase-badges')) return 'block:phase-badges';
  if (el.classList?.contains('mc-invite')) return 'region:invite';
  if (el.classList?.contains('mc-copy')) return 'region:copy';
  if (el.classList?.contains('mc-gallery')) return 'region:gallery';
  if (el.matches?.('figure.mc-photo')) {
    const img = el.querySelector('img[data-photo-slot]');
    if (img) return `figure:${img.getAttribute('data-photo-slot')}`;
  }
  return null;
}

export function resolveElementByKey(root, key) {
  if (!root || !key) return null;
  if (key.startsWith('slot:')) {
    return root.querySelector(`img[data-photo-slot="${CSS.escape(key.slice(5))}"]`);
  }
  if (key.startsWith('figure:')) {
    const img = root.querySelector(`img[data-photo-slot="${CSS.escape(key.slice(7))}"]`);
    return img?.closest('figure.mc-photo') || img;
  }
  if (key === 'logo:brand') return findLogoImage(root);
  if (key === 'block:badges') return root.querySelector('.mc-badges');
  if (key === 'block:phase-badges') return root.querySelector('.mc-phase-badges');
  if (key === 'region:invite') return root.querySelector('.mc-invite');
  if (key === 'region:copy') return root.querySelector('.mc-copy');
  if (key === 'region:gallery') return root.querySelector('.mc-gallery');
  const byEditable = root.querySelector(`[data-editable="${CSS.escape(key)}"]`);
  if (byEditable) return byEditable;
  return root.querySelector(`[data-mc-eid="${CSS.escape(key)}"]`);
}

export function findSelectableElement(target, host) {
  if (!target || !host || !host.contains(target)) return null;
  if (target.closest?.('.mc-slot-caption')) return null;

  const phaseBadge = target.closest?.('.mc-phase-badges');
  if (phaseBadge && host.contains(phaseBadge)) return phaseBadge;

  const badges = target.closest?.('.mc-badges');
  if (badges && host.contains(badges)) return badges;

  const editable = target.closest?.('[data-editable]');
  if (editable && host.contains(editable)) return editable;

  const logoImg = target.closest?.('img');
  if (logoImg && isLogoImage(logoImg) && host.contains(logoImg)) return logoImg;

  const copyRegion = target.closest?.('.mc-copy');
  const galleryRegion = target.closest?.('.mc-gallery');
  const invite = target.closest?.('.mc-invite');

  if (target.matches?.('img[data-photo-slot]')) return target;
  const figure = target.closest?.('.mc-gallery figure.mc-photo');
  if (figure && figure.contains(target)) {
    const img = figure.querySelector('img[data-photo-slot]');
    if (target === figure || target === img) return img || figure;
  }

  if (galleryRegion && (target === galleryRegion || !target.closest?.('img[data-photo-slot]'))) {
    return galleryRegion;
  }
  if (copyRegion && (target === copyRegion || !target.closest?.('[data-editable]'))) {
    if (target.closest?.('.mc-phase-badges')) return target.closest('.mc-phase-badges');
    if (target.closest?.('.mc-logo-wrap')) return findLogoImage(host);
    if (target === copyRegion) return copyRegion;
  }
  if (invite && target === invite) return invite;

  return null;
}

export function normalizeArtboardDom(root) {
  if (!root) return;
  root.querySelectorAll('figure[data-photo-slot]').forEach((fig) => {
    fig.removeAttribute('data-photo-slot');
  });
  const logo = findLogoImage(root);
  if (logo && !logo.hasAttribute('data-logo-slot')) {
    logo.setAttribute('data-logo-slot', 'brand');
  }
}

export function buildDomRegistry(root, { photoSlots = [] } = {}) {
  /** @type {RegistryEntry[]} */
  const entries = [];
  if (!root) return entries;

  const invite = root.querySelector('.mc-invite') || root;
  REGION_MAP.forEach(({ key, selector, label, group }) => {
    if (invite.querySelector(selector)) entries.push({ key, label, kind: 'region', group });
  });

  BLOCK_SELECTORS.forEach(({ key, selector, label, group }) => {
    if (invite.querySelector(selector)) entries.push({ key, label, kind: 'block', group });
  });

  if (findLogoImage(invite)) {
    entries.push({ key: 'logo:brand', label: 'Logo', kind: 'logo', group: 'Brand' });
  }

  invite.querySelectorAll('[data-editable]').forEach((el) => {
    const key = el.getAttribute('data-editable');
    if (!key) return;
    const tag = el.tagName?.toLowerCase() || 'text';
    entries.push({
      key,
      label: `${key} (${tag})`,
      kind: 'text',
      group: 'Copy',
    });
  });

  const slotLabels = Object.fromEntries(
    (photoSlots || []).map((s) => [s.id, s.label || s.id]),
  );
  invite.querySelectorAll('img[data-photo-slot]').forEach((img) => {
    const sid = img.getAttribute('data-photo-slot');
    if (!sid) return;
    entries.push({
      key: `slot:${sid}`,
      label: slotLabels[sid] || sid,
      kind: 'photo',
      group: 'Photos',
    });
    const fig = img.closest('figure.mc-photo');
    if (fig) {
      entries.push({
        key: `figure:${sid}`,
        label: `${slotLabels[sid] || sid} frame`,
        kind: 'figure',
        group: 'Photos',
      });
    }
  });

  return entries;
}

export function registryLabel(key, entries) {
  const hit = entries.find((e) => e.key === key);
  return hit?.label || key;
}
